import { useEffect, useState, useRef } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Snackbar,
  Typography
} from '@mui/material'
import { rpMinimumMessages } from 'config/config'
import { RTVIClient } from '@pipecat-ai/client-js'
import { DailyTransport } from '@pipecat-ai/daily-transport'
import { apiVersion, serverEndpoint } from 'config/config'
import { useTranslation } from 'react-i18next'
import { PUBLIC_ROLE_PLAYS_URL, ROLE_PLAYS_URL } from 'services/constants'
import { getLocalUser, getPublicUserWithToken } from 'utils/localStorage'
import UnavoidableConfirmCancel from 'components/molecules/notificationOverlay/UnavoidableConfirmCancel'
import { botName, padNumber } from 'utils/formatText'
import { useTimer } from 'react-timer-hook'
import colors from 'styles/colors'
import InCardStack from 'components/atoms/container/InCardStack'
import voiceRecordingHelper from 'services/voiceRecordingHelper'
import { sortByTimestamp } from 'utils/collectionHelper'
import moment from 'moment'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import RowStack from 'components/atoms/container/RowStack'

const CallControls = ({ timer, quitCall, endCall }) => {
  const { t } = useTranslation()
  // If timer less than 2 minutes, set startWrapping to true
  const startWrapping = timer.totalSeconds <= 120

  return (
    <InCardStack spacing={8}>
      <BoxWithBackground
        sx={{
          minWidth: 60,
          padding: '4px 6px',
          borderRadius: 1,
          backgroundColor: startWrapping
            ? colors.Watermelon400
            : colors.MidnightTeal300
        }}>
        <RowStack spacing={4} justifyContent='center'>
          <Typography
            variant='body1'
            color={startWrapping ? colors.Peach50 : colors.White}
            lineHeight={'140%'}>
            {`${padNumber(timer.minutes)}:${padNumber(timer.seconds)}`}
            {timer.minutes === 10 && ` (max)`}
          </Typography>

          {startWrapping && (
            <Typography variant='body1Small' color={colors.Peach50}>
              {t('rolePlay.startWrappingUp')}
            </Typography>
          )}
        </RowStack>
      </BoxWithBackground>
      <Button onClick={endCall} variant='contained' color='secondary'>
        {t('rolePlay.endCallGetFeedback')}
      </Button>
      <Button
        onClick={quitCall}
        variant='text'
        sx={{ color: colors.White }}
        size={'small'}>
        {t('rolePlay.quit')}
      </Button>
    </InCardStack>
  )
}

const RTVICall = ({
  isPublicRolePlay,
  rolePlay,
  started,
  setStarted,
  handleCompletion,
  client,
  setClient
}) => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isMicAlert, setIsMicAlert] = useState(false)
  const [transportState, setTransportState] = useState(null)
  const [endCall, setEndCall] = useState(false)
  const [limitReached, setLimitReached] = useState(false)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const recordingHelper = useRef(null)
  const popupWidth = isPublicRolePlay ? 550 : 440

  // Set expiry time to 10 minutes from now
  const getExpiryTime = () => {
    const time = new Date()
    time.setMinutes(time.getMinutes() + 10)
    return time
  }

  const countDown = useTimer({
    autoStart: false,
    expiryTimestamp: getExpiryTime()
  })

  const rolePlayEndpoint = isPublicRolePlay
    ? PUBLIC_ROLE_PLAYS_URL
    : ROLE_PLAYS_URL
  const isMicrophoneAvailable = async () => {
    try {
      await navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then(stream => {
          stream.getTracks().forEach(track => track.stop())
        })
      return true
    } catch (err) {
      return false
    }
  }

  useEffect(() => {
    return () => {
      if (recordingHelper?.current) {
        recordingHelper?.current?.stopRecording()
      }
    }
  }, [])

  const getLastIndex = array => {
    return array.length > 0 ? array.length - 1 : 0
  }

  useEffect(() => {
    const handleConnect = async () => {
      try {
        const transcriptMessages = []
        let botMessageIndex = null
        const transport = new DailyTransport()
        const newClient = new RTVIClient({
          transport,
          params: {
            baseUrl: `${serverEndpoint}/api/${apiVersion}/${rolePlayEndpoint}/${rolePlay.id}`,
            endpoints: {
              connect: '/bot'
            },
            headers: new Headers({
              'Content-Type': 'application/json',
              Authorization: isPublicRolePlay
                ? getPublicUserWithToken()?.token
                : getLocalUser()?.token
            })
          },
          enableMic: true,
          enableCam: false,
          callbacks: {
            onBotTranscript: d => {
              transcriptMessages[botMessageIndex].content = d.text
              setMessages(transcriptMessages)
            },
            onBotStartedSpeaking: () => {
              // onBotTranscript callback is called after onBotStoppedSpeaking which sets the
              // user message. To ensure timestamps are in sync, we need to add a new message here
              const newMessage = {
                role: 'assistant',
                content: '',
                timestamp: moment().format()
              }
              transcriptMessages.push(newMessage)
              botMessageIndex = getLastIndex(transcriptMessages)

              // Stop recording for the user
              if (recordingHelper.current)
                recordingHelper.current.stopRecording()
            },
            onBotStoppedSpeaking: () => {
              // Start only after the bot has completed first message and stops speaking
              if (transcriptMessages.length === 0) return

              const newMessage = {
                words: [],
                role: 'user',
                content: '',
                timestamp: moment().format()
              }
              transcriptMessages.push(newMessage)

              recordingHelper.current = voiceRecordingHelper(
                getLastIndex(transcriptMessages),
                transcriptMessages,
                setMessages,
                isPublicRolePlay
              )
              recordingHelper.current.startRecording()
            },
            onTransportStateChanged: state => {
              if (['connected', 'ready'].includes(state)) {
                countDown.restart(getExpiryTime())
                countDown.start()
              }

              setTransportState(state)
              if (
                ['error', 'disconnecting', 'disconnected'].includes(state) &&
                !dialogOpen &&
                !endCall
              ) {
                closeDialog()
                setLimitReached(true)
              }
            }
          }
        })
        setClient(newClient)
        const isMic = await isMicrophoneAvailable()
        if (!isMic) {
          setIsMicAlert(true)
        } else {
          await newClient.connect()
        }
      } catch (err) {
        console.error(err)
      }
    }
    if (started && !client) {
      handleConnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started])

  const resetBot = async (resetStarted = true) => {
    if (client) {
      await client.disconnect()
      setClient(null)
    }
    setStarted(!resetStarted)
  }

  const handleEndCall = async () => {
    setLoading(true)
    setTimeout(async () => {
      if (messages.length < rpMinimumMessages) {
        setDialogOpen(true)
      } else {
        countDown.pause()
        await resetBot(false)
        // Remove empty messages and sort by timestamp
        const transcriptMessages = sortByTimestamp(
          messages.filter(m => m.content)
        )
        const totalSeconds = 600 - countDown.totalSeconds // Countdown is in reverse
        await handleCompletion(transcriptMessages, totalSeconds)
      }
      setLoading(false)
    }, 1000)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setTimeout(() => {
      setEndCall(false)
    }, 100)
  }

  return (
    <>
      {!client || transportState !== 'ready' || loading ? (
        <CircularProgress color='Background' />
      ) : (
        <CallControls
          timer={countDown}
          transportState={transportState}
          popupWidth={popupWidth}
          isPublicRolePlay={isPublicRolePlay}
          quitCall={() => setDialogOpen(true)}
          endCall={() => {
            setEndCall(true)
            handleEndCall()
          }}
        />
      )}

      <UnavoidableConfirmCancel
        open={dialogOpen}
        stopClicked
        title={t(
          `rolePlay.${endCall ? 'sureYouWantToEndCall' : !isPublicRolePlay ? 'sureYouWantCancelCall' : 'discardCallWith'}`,
          {
            botName: botName(rolePlay).split(' ')[0]
          }
        )}
        subtitle={t(`rolePlay.thisSessionNotSaved`)}
        lowFocusText={
          endCall ? t('rolePlay.resumeCall') : t('common.goBack')
        }
        lowFocusAction={() => {
          closeDialog()
        }}
        highFocusText={t(
          `rolePlay.${endCall ? 'endCall' : !isPublicRolePlay ? 'quit' : 'discardCall'}`
        )}
        highFocusAction={async () => {
          await resetBot()
          closeDialog()
        }}
        textAlignment={isPublicRolePlay ? 'center' : 'left'}
        dialogWidth={popupWidth}
        isPublicRolePlay={isPublicRolePlay}
      />
      <UnavoidableConfirmCancel
        open={limitReached}
        title={t('rolePlay.greatSession')}
        subtitle={t('rolePlay.hit10MinuteLimit')}
        lowFocusText={t('rolePlay.discardCall')}
        lowFocusAction={() => {
          setLimitReached(false)
          resetBot()
        }}
        highFocusText={t('rolePlay.getFeedback')}
        highFocusAction={() => {
          setLimitReached(false)
          handleEndCall()
        }}
        hideHighFocus={messages.length < rpMinimumMessages}
        textAlignment={isPublicRolePlay ? 'center' : 'left'}
        dialogWidth={popupWidth}
        isPublicRolePlay={isPublicRolePlay}
        warning={isPublicRolePlay}
      />
      <Snackbar
        open={isMicAlert}
        sx={{ paddingTop: 10 }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={6000}
        onClick={() => setIsMicAlert(false)}
        onClose={() => setIsMicAlert(false)}>
        <Alert
          sx={{
            userSelect: 'none',
            textWrap: 'nowrap',
            borderRadius: 3
          }}
          onClose={() => setIsMicAlert(false)}
          severity='error'>
          {t('rolePlay.pleaseEnableTheMic')}
        </Alert>
      </Snackbar>
    </>
  )
}
export default RTVICall
